// @ts-strict-ignore
import { DateInputBlock, DateRangeInputBlock } from '@coast/schemas';
import { useCallback, useContext, useMemo } from 'react';
import { CardFormContext } from '@shared/components/blocks/contexts/form';
import { i18n } from '@shared/util/i18n';
import { useDateRangeDefaultValues } from './input/hooks';
import { MBCardInputBlockDate } from './inputDate';

interface Props {
  block: DateRangeInputBlock;
}

export function createStartDateInputBlock(block: DateRangeInputBlock): DateInputBlock {
  return {
    type: 'input',
    name: block.startDateFieldName ?? 'startDate',
    fieldType: block.mode,
    placeholder: block.startDateFieldLabel ?? i18n('card.blocks.dateRangeInput.startDateLabel'),
    textStyle: 'body1',
    required: true,
    label: block.startDateFieldLabel ?? i18n('card.blocks.dateRangeInput.startDateLabel'),
    defaultTime: block.defaultTime,
    validation: {
      message: i18n('card.blocks.dateRangeInput.startDateError'),
    },
    autoFocus: false,
    icon: 'calendar',
  };
}

export function createEndDateInputBlock(block: DateRangeInputBlock): DateInputBlock {
  return {
    type: 'input',
    name: block.endDateFieldName ?? 'endDate',
    fieldType: block.mode,
    placeholder: block.endDateFieldLabel ?? i18n('card.blocks.dateRangeInput.endDateLabel'),
    textStyle: 'body1',
    required: true,
    label: block.endDateFieldLabel ?? i18n('card.blocks.dateRangeInput.endDateLabel'),
    defaultTime: block.defaultTime,
    validation: {
      message: i18n('card.blocks.dateRangeInput.endDateError'),
    },
    autoFocus: false,
    icon: 'calendar',
  };
}

export function MBCardInputBlockDateRange({ block }: Props) {
  const startDateInputBlock = useMemo(() => createStartDateInputBlock(block), [block]);
  const endDateInputBlock = useMemo(() => createEndDateInputBlock(block), [block]);

  const { values, setFieldValue } = useContext(CardFormContext);
  const startDate = values[startDateInputBlock.name];
  const endDate = values[endDateInputBlock.name];

  const setStartDate = useCallback(
    (value: string) => setFieldValue(startDateInputBlock.name, value),
    [startDateInputBlock.name, setFieldValue],
  );
  const setEndDate = useCallback(
    (value: string) => setFieldValue(endDateInputBlock.name, value),
    [endDateInputBlock.name, setFieldValue],
  );

  useDateRangeDefaultValues(
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    block.delta?.unit ?? 'day',
    block.delta?.count ?? 1,
  );

  return (
    <>
      <MBCardInputBlockDate block={startDateInputBlock} />
      <MBCardInputBlockDate block={endDateInputBlock} />
    </>
  );
}
